import { useCallback, useEffect, useRef } from "react";
import { useNavigate, Navigate, Outlet } from "react-router-dom";

import useIsAuthorized from "Hooks/useIsAuthorized";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";

/** @component This wraps all of the routes in App that require user authentication, to handle
 * page refreshes when state is lost. It will direction the user to either Login if no valid
 * JWT is present, or render the requested route if there is a valid JWT present.
 * It also adds the keyboard shortcuts for navigating to different pages.
 */
export default function PrivateRoute() {
    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();

    const navigate = useRef(useNavigate());

    useIsAuthorized();

    /** Keydown event handler for keyboard shortcuts
     *
     * Memoized with useCallback since including the function inside the useEffect
     * caused different instances of event listeners to be created that could not be
     * removed by the cleanup function */
    const handleKeydown = useCallback(
        (e: KeyboardEvent) => {
            if (e.altKey) {
                switch (e.key) {
                    case "l":
                        navigate.current("/run_details/latest");
                        break;
                    case "r":
                        navigate.current("/run");
                        break;
                    case "c":
                        if (userContext.companies > 1) {
                            navigate.current("/login_company");
                        } else {
                            newAlert({
                                message:
                                    "Unable to switch companies - only one available",
                                color: "danger",
                            });
                        }
                        break;
                    case "h":
                        navigate.current("/integrations");
                        break;
                }
            }
        },
        [newAlert, userContext.companies]
    );

    // Add keyboard shortcuts if/when user is authenticated
    useEffect(() => {
        if (userContext.isAuthenticated) {
            document.addEventListener("keydown", handleKeydown);

            // Cleanup function
            return () => document.removeEventListener("keydown", handleKeydown);
        }
    }, [handleKeydown, userContext.isAuthenticated]);

    // Undefined is a pending state to do nothing until we know whether the user is authenticated
    if (userContext.isAuthenticated === undefined) {
        return null;
    } else if (userContext.isAuthenticated) {
        return <Outlet />;
    } else {
        return <Navigate to={"/login"} />;
    }
}
